import {
  type Briefing,
  type CreateBriefing,
  type EstateFileEntry,
  type EstateSnapshot,
  formatUsd,
} from '@acasa/shared'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useEstateLookup } from '@/features/handovers/use-handovers'
import { ApiRequestError } from '@/lib/api'
import { briefingAddress, fileLabel, formatBytes, formatDate } from './format'
import { LimitNotice } from './limit-notice'
import {
  useBriefingEstimate,
  useBriefings,
  useCreateBriefing,
  useEstateFiles,
} from './use-briefings'
import { useDialogFocus } from './use-dialog-focus'

/** The object a briefing is for. Only an object onOffice knows by id can have one. */
export type BriefingEstateChoice = {
  estateId: number
  externalNumber: string | null
  label: string
}

/** The object a stored briefing names, for prefilling a new one. Null without an onOffice id. */
export function estateChoiceOf(briefing: Briefing): BriefingEstateChoice | null {
  const { estate } = briefing
  if (estate.estateId === null) return null
  return {
    estateId: estate.estateId,
    externalNumber: estate.externalNumber,
    label: briefingAddress(estate) ?? estate.title ?? 'ohne Adresse',
  }
}

const UNAVAILABLE = 'Der Briefing-Dienst ist gerade nicht erreichbar oder nicht konfiguriert.'

/**
 * A 503 WITH an error envelope is the API speaking about the provider chosen
 * under Einstellungen — no key on this server, a key the provider rejected, a
 * model it does not know — and its sentence is authoritative. A 503 WITHOUT
 * one (`unknown_error`: a proxy's HTML page during a redeploy, say) proves
 * nothing about the key, so it gets a neutral line.
 */
function errorText(error: unknown): string {
  if (error instanceof ApiRequestError && error.status === 503) {
    return error.code === 'unknown_error' ? UNAVAILABLE : error.message
  }
  return error instanceof Error ? error.message : 'Erstellen fehlgeschlagen'
}

const ESTIMATE_DEBOUNCE_MS = 500

const GROUPS: { category: EstateFileEntry['category']; title: string }[] = [
  { category: 'internal', title: 'Intern' },
  { category: 'external', title: 'Extern' },
  { category: null, title: 'Ohne Kategorie' },
]

const label = 'text-faint mb-2 block text-[10px] tracking-[0.14em] uppercase'

function snapshotLabel(match: EstateSnapshot): string {
  const street = [match.street, match.houseNumber?.split(',')[0]?.trim()].filter(Boolean).join(' ')
  return [street, match.city].filter(Boolean).join(', ') || match.title || 'ohne Adresse'
}

/** The input settles for half a second before it is priced. */
function useDebounced<T>(value: T, key: string, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  const latest = useRef(value)
  latest.current = value

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialised value on purpose — the object is new on every render
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(latest.current), delay)
    return () => clearTimeout(timer)
  }, [key, delay])

  return debounced
}

/** Step 1 — find the object. The protocols' lookup: mirror first, then onOffice live. */
function EstateStep({ onPick }: { onPick: (estate: BriefingEstateChoice) => void }) {
  const [term, setTerm] = useState('')
  const lookup = useEstateLookup(term)

  return (
    <div className="mt-4">
      <label className="block">
        <span className={label}>Objekt-Nr. oder Bezeichnung</span>
        <input
          value={term}
          // biome-ignore lint/a11y/noAutofocus: the step exists to type an object number
          autoFocus
          onChange={(event) => setTerm(event.target.value)}
          placeholder="z. B. 2024-103"
          className="border-line focus:border-brand bg-canvas text-ink placeholder:text-faint w-full border px-3 py-2.5 text-sm outline-none"
        />
      </label>

      {lookup.isFetching && <p className="text-faint mt-2 text-[11px]">sucht …</p>}

      {lookup.data && lookup.data.matches.length > 0 && (
        <ul className="border-line mt-2 max-h-64 overflow-auto border">
          {lookup.data.matches.map((match) => {
            const estateId = match.estateId
            return (
              <li key={`${match.estateId}-${match.externalNumber}`}>
                <button
                  type="button"
                  disabled={estateId === null}
                  onClick={() =>
                    estateId !== null &&
                    onPick({
                      estateId,
                      externalNumber: match.externalNumber,
                      label: snapshotLabel(match),
                    })
                  }
                  className="hover:bg-canvas block w-full px-3 py-2 text-left text-xs transition-colors disabled:opacity-50"
                >
                  <span className="text-ink font-mono">{match.externalNumber ?? '—'}</span>
                  <span className="text-muted"> · {match.title ?? 'ohne Bezeichnung'}</span>
                  <span className="text-faint block">
                    {snapshotLabel(match)}
                    {match.fetchedLive && ' · direkt aus onOffice'}
                    {estateId === null && ' · ohne onOffice-ID, kein Briefing möglich'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {lookup.data && term.trim().length >= 2 && lookup.data.matches.length === 0 && (
        <p className="text-muted mt-2 text-[11px]">Kein Objekt gefunden.</p>
      )}
    </div>
  )
}

function FileRow({
  file,
  checked,
  onToggle,
}: {
  file: EstateFileEntry
  checked: boolean
  onToggle: () => void
}) {
  const meta = [
    file.extension?.toUpperCase() ?? file.type,
    formatBytes(file.sizeBytes),
    formatDate(file.modifiedAt),
    file.documentAttribute,
  ].filter(Boolean)

  return (
    <li>
      <label
        className={`flex items-start gap-3 px-3 py-2 text-xs ${
          file.selectable ? 'hover:bg-canvas cursor-pointer' : ''
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={!file.selectable}
          onChange={onToggle}
          className="accent-brand mt-0.5 size-3.5 shrink-0 disabled:opacity-40"
        />
        <span className="min-w-0 flex-1">
          {/* Only the box and the name are dimmed: the meta line is already
              faint, and fading it further put it below a readable contrast. */}
          <span className={`block break-words ${file.selectable ? 'text-ink' : 'text-muted'}`}>
            {fileLabel(file)}
          </span>
          <span className="text-faint tnum mt-0.5 block text-[11px]">{meta.join(' · ')}</span>
        </span>
        {!file.selectable && (
          <span className="text-faint shrink-0 text-[10px]">nur PDF oder Word</span>
        )}
      </label>
    </li>
  )
}

/**
 * Step 2 — which documents, which questions, and what it will cost.
 *
 * The estimate is the server's: it downloads the selected files and counts them,
 * so it waits until the input has been still for half a second, and is cached
 * per selection. Only the billed figure is shown (D7).
 */
function FilesStep({
  estate,
  initialFileIds,
  initialSpecialQuestions,
  create,
  onBack,
  onCancel,
}: {
  estate: BriefingEstateChoice
  initialFileIds: number[] | null
  initialSpecialQuestions: string | null
  create: ReturnType<typeof useCreateBriefing>
  onBack: (() => void) | null
  onCancel: () => void
}) {
  const navigate = useNavigate()
  const questionsId = useId()
  const files = useEstateFiles(estate.estateId)
  // Only for the admin's default Sonderfragen; the list is usually cached.
  const briefings = useBriefings()

  const [selection, setSelection] = useState<Set<number> | null>(null)
  // Null until edited: shows the earlier run's questions, else the admin's
  // default once the list has answered — still editable either way.
  const [edited, setEdited] = useState<string | null>(initialSpecialQuestions)
  const questions = edited ?? briefings.data?.defaultSpecialQuestions ?? ''
  const hasDefault = (briefings.data?.defaultSpecialQuestions ?? '').trim() !== ''

  const list = files.data?.available ? files.data.files : null

  // The first selection: the run being repeated, or onOffice's preselection.
  useEffect(() => {
    if (!list || selection !== null) return
    const selectable = new Set(list.filter((file) => file.selectable).map((file) => file.id))
    setSelection(
      new Set(
        initialFileIds
          ? initialFileIds.filter((id) => selectable.has(id))
          : list.filter((file) => file.selectable && file.preselected).map((file) => file.id),
      ),
    )
  }, [list, selection, initialFileIds])

  const missingFromRerun = useMemo(() => {
    if (!list || !initialFileIds) return 0
    const present = new Set(list.filter((file) => file.selectable).map((file) => file.id))
    return initialFileIds.filter((id) => !present.has(id)).length
  }, [list, initialFileIds])

  const selected = useMemo(
    () => (list ?? []).filter((file) => selection?.has(file.id)),
    [list, selection],
  )

  const input: CreateBriefing | null =
    selected.length > 0
      ? {
          estateId: estate.estateId,
          fileIds: selected.map((file) => file.id).sort((a, b) => a - b),
          specialQuestions: questions.trim() || null,
        }
      : null
  const inputKey = JSON.stringify(input)
  const debounced = useDebounced(input, inputKey, ESTIMATE_DEBOUNCE_MS)
  const settling = JSON.stringify(debounced) !== inputKey
  const estimate = useBriefingEstimate(settling ? null : debounced)

  const estimateError = estimate.error instanceof ApiRequestError ? estimate.error : null
  const limitHit =
    estimateError?.code === 'ai_limit_reached' ||
    (!settling && estimate.data?.limitReached === true) ||
    (create.error instanceof ApiRequestError && create.error.code === 'ai_limit_reached')
  // A refused input would be refused again on create; the server named the file.
  const inputRefused =
    !settling && estimateError !== null && [400, 409, 422].includes(estimateError.status)

  // At 80 % of the month's limit the dialog says so before the click (§8).
  const budgetShare = (() => {
    const data = estimate.data
    if (!data || data.monthLimitMicroUsd === null || data.monthRemainingMicroUsd === null) {
      return null
    }
    if (data.monthLimitMicroUsd <= 0 || limitHit) return null
    const share =
      (data.monthLimitMicroUsd - data.monthRemainingMicroUsd + data.billedMicroUsd) /
      data.monthLimitMicroUsd
    return share >= 0.8 ? share : null
  })()

  const toggle = (id: number) =>
    setSelection((previous) => {
      const next = new Set(previous ?? [])
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const submit = async () => {
    if (!input) return
    const briefing = await create.mutateAsync(input)
    onCancel()
    navigate(`/briefings/${briefing.id}`)
  }

  const totalBytes = selected.reduce((sum, file) => sum + (file.sizeBytes ?? 0), 0)

  return (
    <>
      <div className="border-line bg-canvas mt-4 flex flex-wrap items-baseline justify-between gap-2 border px-3 py-2">
        <span className="min-w-0 text-xs">
          <span className="text-ink font-mono">
            {estate.externalNumber ?? `#${estate.estateId}`}
          </span>
          <span className="text-muted"> · {estate.label}</span>
        </span>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-faint hover:text-ink text-[11px] underline underline-offset-2"
          >
            anderes Objekt
          </button>
        )}
      </div>

      <fieldset className="mt-5">
        <legend className={label}>Unterlagen aus onOffice</legend>

        {files.isPending && (
          <p className="text-faint text-xs">Dateiliste wird aus onOffice gelesen …</p>
        )}

        {(files.isError || files.data?.available === false) && (
          <div className="border-danger/30 bg-danger/5 flex flex-wrap items-center justify-between gap-2 border px-3 py-2">
            <p className="text-danger text-xs">onOffice nicht erreichbar.</p>
            <button
              type="button"
              disabled={files.isFetching}
              onClick={() => void files.refetch()}
              className="border-line text-muted hover:text-ink bg-surface border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-40"
            >
              {files.isFetching ? 'wird versucht …' : 'Erneut versuchen'}
            </button>
          </div>
        )}

        {list && list.length === 0 && (
          <p className="text-muted text-xs">
            In onOffice sind diesem Objekt keine Dateien zugeordnet.
          </p>
        )}

        {list && list.length > 0 && (
          <div className="border-line max-h-[50vh] overflow-auto border">
            {GROUPS.map((group) => {
              const rows = list.filter((file) => file.category === group.category)
              if (rows.length === 0) return null
              return (
                <div key={group.title}>
                  <p className="bg-raised text-faint border-line sticky top-0 border-b px-3 py-1 text-[10px] tracking-[0.14em] uppercase">
                    {group.title} · {rows.length}
                  </p>
                  <ul className="divide-line divide-y">
                    {rows.map((file) => (
                      <FileRow
                        key={file.id}
                        file={file}
                        checked={selection?.has(file.id) ?? false}
                        onToggle={() => toggle(file.id)}
                      />
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        )}

        {list && (
          <p className="text-muted tnum mt-2 text-[11px]">
            {selected.length} {selected.length === 1 ? 'Datei' : 'Dateien'} ·{' '}
            {formatBytes(totalBytes)}
            {missingFromRerun > 0 &&
              ` · ${missingFromRerun} ${
                missingFromRerun === 1 ? 'Datei' : 'Dateien'
              } des letzten Briefings nicht mehr in onOffice`}
          </p>
        )}
      </fieldset>

      <div className="mt-5">
        <label htmlFor={questionsId} className={label}>
          Sonderfragen
        </label>
        <textarea
          id={questionsId}
          value={questions}
          onChange={(event) => setEdited(event.target.value)}
          maxLength={2000}
          rows={2}
          aria-describedby={`${questionsId}-hint`}
          placeholder="z. B. Leitungen, Aufzug, Dach — durch Komma getrennt"
          className="border-line focus:border-brand bg-canvas text-ink placeholder:text-faint w-full border px-3 py-2.5 text-sm outline-none"
        />
        <span id={`${questionsId}-hint`} className="text-faint mt-1 block text-[11px]">
          {hasDefault
            ? 'Vorbelegt mit den Standard-Sonderfragen aus den Einstellungen. Leer gelassen gelten ebenfalls diese.'
            : 'Leer gelassen gelten die Standard-Sonderfragen aus den Einstellungen.'}
        </span>
      </div>

      <div className="mt-5 space-y-2" aria-live="polite">
        {input === null && list && list.length > 0 && (
          <p className="text-faint text-xs">Mindestens eine Datei auswählen.</p>
        )}
        {input !== null && (settling || estimate.isFetching) && (
          <p className="text-faint text-xs">
            Kosten werden geschätzt — die Unterlagen werden gelesen …
          </p>
        )}
        {input !== null && !settling && !estimate.isFetching && estimate.data && (
          <p className="text-ink tnum text-xs">
            Geschätzte Kosten: ca. {formatUsd(estimate.data.billedMicroUsd)} ·{' '}
            {estimate.data.documents} {estimate.data.documents === 1 ? 'Dokument' : 'Dokumente'}
            {estimate.data.pages !== null && ` · ${estimate.data.pages} Seiten`}
            {estimate.data.monthRemainingMicroUsd !== null && (
              <span className="text-muted block text-[11px]">
                Verbleibendes Budget diesen Monat:{' '}
                {formatUsd(Math.max(0, estimate.data.monthRemainingMicroUsd))}
              </span>
            )}
          </p>
        )}
        {input !== null && !settling && !estimate.isFetching && budgetShare !== null && (
          <p className="border-warning/40 bg-warning/10 text-ink border px-3 py-2 text-xs">
            Mit diesem Briefing sind ca. {Math.min(100, Math.round(budgetShare * 100))} % des
            Monatslimits für KI-Nutzung verbraucht.
          </p>
        )}
        {limitHit && (
          <LimitNotice
            message={estimateError?.code === 'ai_limit_reached' ? estimateError.message : undefined}
          />
        )}
        {!settling && estimateError && estimateError.code !== 'ai_limit_reached' && (
          <p className="text-danger text-xs">{errorText(estimateError)}</p>
        )}
        {create.isError &&
          !(
            create.error instanceof ApiRequestError && create.error.code === 'ai_limit_reached'
          ) && <p className="text-danger text-xs">{errorText(create.error)}</p>}
      </div>

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="button"
          disabled={input === null || create.isPending || limitHit || inputRefused}
          onClick={() => void submit().catch(() => undefined)}
          className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
        >
          {create.isPending ? 'wird erstellt …' : 'Erstellen'}
        </button>
      </div>
    </>
  )
}

/**
 * "Neues Briefing": the object (skipped when it is already known), then the
 * documents and the Sonderfragen. A re-run passes the earlier run's files and
 * questions; it still creates a new briefing — they are never edited (D11).
 */
export function CreateBriefingDialog({
  estate: initialEstate = null,
  initialFileIds = null,
  initialSpecialQuestions = null,
  onClose,
}: {
  estate?: BriefingEstateChoice | null
  initialFileIds?: number[] | null
  initialSpecialQuestions?: string | null
  onClose: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const [estate, setEstate] = useState<BriefingEstateChoice | null>(initialEstate)
  const create = useCreateBriefing()
  useDialogFocus(panel, onClose, { closable: !create.isPending })

  // Portalled: opened from inside the Unterlagen matrix, the dialog would
  // otherwise live in a table cell under the table's own stacking context.
  return createPortal(
    <div className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-surface border-line max-h-full w-full max-w-2xl overflow-auto border p-5 outline-none"
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 id={titleId} className="font-display text-ink text-lg">
            {initialFileIds ? 'Briefing erneut erstellen' : 'Neues Briefing'}
          </h2>
          <span className="text-faint text-[11px]">
            Schritt {estate ? 2 : 1} von 2 · {estate ? 'Unterlagen' : 'Objekt'}
          </span>
        </div>

        {estate ? (
          <FilesStep
            key={estate.estateId}
            estate={estate}
            initialFileIds={estate.estateId === initialEstate?.estateId ? initialFileIds : null}
            initialSpecialQuestions={initialSpecialQuestions}
            create={create}
            onBack={initialEstate ? null : () => setEstate(null)}
            onCancel={onClose}
          />
        ) : (
          <>
            <EstateStep onPick={setEstate} />
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
